import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { useSEO } from "@/lib/seo";
import { SITE } from "@/lib/site";
import { CreditCard, Store, ShieldCheck } from "lucide-react";

const Section = ({ heading, children, testid }) => (
  <section className="mt-12" data-testid={testid}>
    <h2 className="font-display text-xl md:text-2xl text-neutral-900">{heading}</h2>
    <div className="mt-4 space-y-3 text-sm md:text-base text-neutral-600 leading-relaxed">{children}</div>
  </section>
);

export default function FinancingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: "Financing & Payment Options | Gold Time Jewelry",
    canonicalPath: "/financing",
    description:
      "Pay over time online with Affirm or Klarna on eligible orders, or apply for Acima and Snap Finance in-store. Gold Time Jewelry — Katy, Copperas Cove & Killeen, TX.",
  });

  return (
    <main data-testid="financing-page">
      <Header />
      <section className="relative overflow-hidden pt-[164px] pb-24 lg:pt-[196px] lg:pb-32">
        <PatternLayer pattern={patternLight} />
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow>Payment Options</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="financing-heading">
              Financing &amp; <span className="text-[#C9A227]">payment options</span>
            </h1>
            <p className="text-sm md:text-base text-neutral-600 leading-relaxed mt-6">
              Fine jewelry should fit your budget as comfortably as it fits you. Here are the ways to pay at
              Gold Time Jewelry — online and in our Texas showrooms.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <Section heading="Pay online" testid="financing-online">
              <div className="flex items-start gap-3">
                <CreditCard size={18} strokeWidth={1.5} className="text-[#C9A227] mt-1 shrink-0" />
                <p>
                  <span className="font-semibold text-neutral-800">Affirm</span> and{" "}
                  <span className="font-semibold text-neutral-800">Klarna</span> are available on eligible
                  orders in our secure online checkout. Choose either one on the payment page to see the exact
                  payment plans they offer for your order — eligibility, schedule and terms are always shown
                  by the provider before you confirm.
                </p>
              </div>
              <p>
                We also accept major credit and debit cards through our secure checkout. Every online order
                ships fully insured, or can be shipped to a Gold Time showroom for pickup.
              </p>
            </Section>

            <Section heading="In-store financing" testid="financing-instore">
              <div className="flex items-start gap-3">
                <Store size={18} strokeWidth={1.5} className="text-[#C9A227] mt-1 shrink-0" />
                <p>
                  Shopping with us in person? <span className="font-semibold text-neutral-800">Acima</span> and{" "}
                  <span className="font-semibold text-neutral-800">Snap Finance</span> are available at all
                  three Gold Time showrooms. Our team will walk you through the application in-store — visit
                  us in Katy, Copperas Cove or Killeen.
                </p>
              </div>
              <p className="text-xs text-neutral-500">
                Acima and Snap Finance are in-store options only and are not available in online checkout.
              </p>
            </Section>

            <Section heading="Good to know" testid="financing-notes">
              <div className="flex items-start gap-3">
                <ShieldCheck size={18} strokeWidth={1.5} className="text-[#C9A227] mt-1 shrink-0" />
                <p>
                  Financing providers set their own eligibility and terms. Gold Time Jewelry does not set or
                  quote approval decisions, rates or payment schedules — you will always see the provider's
                  own offer before committing to anything.
                </p>
              </div>
            </Section>

            <p className="mt-10 text-sm text-neutral-600 leading-relaxed" data-testid="financing-categories">
              Shopping for something specific? Browse{" "}
              <Link to="/collections/wedding-bands" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">wedding bands</Link>,{" "}
              <Link to="/collections/diamond-rings" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">diamond rings</Link>,{" "}
              <Link to="/collections/cuban-link-chains" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">Cuban chains</Link>{" "}
              and{" "}
              <Link to="/collections/lab-grown-diamonds" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">lab-grown diamonds</Link>{" "}
              — flexible payments are available on eligible orders across the store.
            </p>

            <div className="mt-14 bg-white border border-[#E8DCC4] rounded-sm p-6" data-testid="financing-contact">
              <h2 className="font-display text-lg text-neutral-900">Questions about paying for a piece?</h2>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
                Call or visit any showroom — Katy {SITE.locations[0].phone} · Copperas Cove{" "}
                {SITE.locations[1].phone} · Killeen {SITE.locations[2].phone} — or{" "}
                <Link to="/contact" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">
                  send us a note
                </Link>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </main>
  );
}
